import React, { createContext, useContext, useEffect, useState } from "react";
import { LoadingScreen } from "@administrate/piston-ux";

import { WEBLINK_LOCALES_QUERY } from "../queries/locales";
import { Locale, LocalesResponse } from "../types/Locale";
import { extractNodes } from "../utils/extractNodes";
import { findLocaleByCode, resolveDefaultLocale } from "../utils/localeHelpers";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useOptionalWebLinkClient } from "../hooks/weblink";
import { useGlobalStore } from "./GlobalStore";
import { onContentLocaleChanged } from "../utils/contentLocaleChangeHandler";

type ContentLocaleContextType = {
  /** The supported Locales for this instance, empty if they could not be loaded. */
  locales: Locale[];
  /** The Locale content is shown in, undefined if there are no Locales. */
  contentLocale?: Locale;
  setContentLocale: (locale: Locale) => void;
};

const ContentLocaleContext = createContext<
  ContentLocaleContextType | undefined
>(undefined);

export const useContentLocale = () => {
  const context = useContext(ContentLocaleContext);
  if (context === undefined) {
    throw new Error("ContentLocaleProvider not found");
  }
  return context;
};

/**
 * Tracks the Locale that course content is requested in. The learner's choice
 * is kept in local storage for this browser only - it is deliberately not
 * saved against their Contact, which is where their interface language lives.
 *
 * Children are held back until the Locales have loaded so that no course query
 * can run before the Locale to request it in is known.
 */
export const ContentLocaleProvider: React.FunctionComponent<
  React.PropsWithChildren<unknown>
> = ({ children }) => {
  const globalStore = useGlobalStore();
  const client = useOptionalWebLinkClient();
  const [locales, setLocales] = useState<Locale[]>([]);
  const [areLocalesLoading, setAreLocalesLoading] = useState(!!client);
  const [selectedCode, setSelectedCode] = useLocalStorage<string | undefined>(
    `contentLocale:${globalStore.config.brand}`,
  );

  useEffect(() => {
    if (!client) {
      setAreLocalesLoading(false);
      return;
    }

    let isCurrent = true;
    setAreLocalesLoading(true);

    client
      .query<LocalesResponse>({ query: WEBLINK_LOCALES_QUERY })
      .then((result) => {
        // With the instance's multilingual courses feature flag off the Locales
        // stay empty, which hides the picker and stops the Locale header.
        if (isCurrent && result.data?.store?.multilingualCoursesEnabled) {
          setLocales(extractNodes(result.data?.locales?.edges ?? []));
        }
      })
      .catch((error) => {
        // Content stays in its authored Locale if we can't offer a choice.
        console.error(error);
      })
      .finally(() => {
        if (isCurrent) {
          setAreLocalesLoading(false);
        }
      });

    return () => {
      isCurrent = false;
    };
  }, [client]);

  const contentLocale =
    findLocaleByCode(locales, selectedCode) ?? resolveDefaultLocale(locales);

  // Read by the WebLink Apollo link on every operation. With fewer than two
  // Locales there is no picker and nothing can be translated, so don't ask
  // the Core API for translations at all.
  globalStore.contentLocaleId =
    locales.length > 1 ? contentLocale?.id : undefined;

  if (areLocalesLoading) {
    return <LoadingScreen />;
  }

  return (
    <ContentLocaleContext.Provider
      value={{
        locales,
        contentLocale,
        // Course content is spread across cached queries and already-rendered
        // pages, so the tree that holds it is rebuilt rather than refetched
        // selectively. The header is read per operation, so the new Locale
        // applies from the first query afterwards.
        setContentLocale: (locale: Locale) => {
          setSelectedCode(locale.code);
          onContentLocaleChanged();
        },
      }}
    >
      {children}
    </ContentLocaleContext.Provider>
  );
};
